import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { UtilityService } from 'src/app/services/utility.service';
import { ToastService } from 'src/app/services/toast.service';
import { stringifyBooleans, toBoolean, formatToApiDate } from 'src/app/shared/utils/form-utils';
import { BaseComponent } from 'src/app/shared/base.component';
import { takeUntil } from 'rxjs/operators';

interface UserPermissionsSet {
  buy: boolean;
  sell: boolean;
}

@Component({
  selector: 'app-user-permissions',
  templateUrl: './user-permissions.component.html',
  styleUrls: ['./user-permissions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserPermissionsComponent extends BaseComponent implements OnInit {

  utilityId = '';
  utilityConfig: any = null;
  showDialog = false;
  isLoadingUserPermissions = true;
  isSavingUserPermissions = false;
  selectedPermissionType: 'consumer' | 'prosumer' | null = null;

  consumerPermissions: UserPermissionsSet = { buy: false, sell: false };
  prosumerPermissions: UserPermissionsSet = { buy: false, sell: false };

  private initialConsumerPermissions: UserPermissionsSet = { buy: false, sell: false };
  private initialProsumerPermissions: UserPermissionsSet = { buy: false, sell: false };

  constructor(
    private utilityService: UtilityService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.utilityId = this.route.snapshot.paramMap.get('id') ?? '';
    this.loadUtilityConfig();
  }

  loadUtilityConfig(silent = false): void {
    if (!silent) this.isLoadingUserPermissions = true;
    this.utilityService.getUtilityConfiguration(this.utilityId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.utilityConfig = data;
          const cfg = data.config;

          this.consumerPermissions = {
            buy: toBoolean(cfg['allow_consumers_to_buy']),
            sell: toBoolean(cfg['allow_consumers_to_sell']),
          };
          this.prosumerPermissions = {
            buy: toBoolean(cfg['allow_prosumers_to_buy']),
            sell: toBoolean(cfg['allow_prosumers_to_sell']),
          };

          this.captureInitialState();
          if (!silent) this.isLoadingUserPermissions = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('[UserPermissions] Load failed:', err);
          if (!silent) this.isLoadingUserPermissions = false;
          this.cdr.markForCheck();
        }
      });
  }

  openDialog(type: 'consumer' | 'prosumer'): void {
    this.selectedPermissionType = type;
    this.showDialog = true;
    this.cdr.markForCheck();
  }

  saveConfiguration(): void {
    if (!this.utilityConfig) return;

    const activeType = this.selectedPermissionType;
    const existingConfig = this.utilityConfig?.config || {};
    const allowedKeys = [
      'enable_monthly_bid', 'enable_day_ahead_bid', 'enable_preferential_bid',
      'allow_prosumers_to_sell', 'allow_prosumers_to_buy',
      'allow_consumers_to_sell', 'allow_consumers_to_buy',
      'allow_utility_to_sell', 'allow_utility_to_buy',
      'enable_fix_price', 'fix_price', 'min_price', 'max_price',
      'maximum_range_in_days', 'day_ahead_in_days', 'hours_left_to_close',
    ];
    const safeExisting: any = {};
    allowedKeys.forEach(key => {
      if (existingConfig[key] !== undefined) safeExisting[key] = existingConfig[key];
    });

    const updatedConfig = stringifyBooleans({
      ...safeExisting,
      allow_prosumers_to_sell: this.prosumerPermissions.sell,
      allow_prosumers_to_buy: this.prosumerPermissions.buy,
      allow_consumers_to_sell: this.consumerPermissions.sell,
      allow_consumers_to_buy: this.consumerPermissions.buy,
    });

    const payload = {
      config: updatedConfig,
      validity_start: formatToApiDate(),
      validity_end: 'infinity',
    };

    this.isSavingUserPermissions = true;
    this.utilityService.updateUtilityConfig(this.utilityId, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSavingUserPermissions = false;
          const label = activeType === 'consumer' ? 'Consumer' : 'Prosumer';
          this.toastService.show(`${label} permissions updated!`);
          this.loadUtilityConfig(true);
        },
        error: (err) => {
          this.isSavingUserPermissions = false;
          console.error('[UserPermissions] Save failed:', err);
          this.toastService.show('Failed to update permissions.', 'error');
          this.cdr.markForCheck();
        },
      });
    this.closeDialog();
  }

  discardChanges(): void {
    if (this.isDirty(this.selectedPermissionType)) {
      const ref = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'custom-dialog-container',
        data: { title: 'Confirmation', message: 'Discard unsaved changes?' },
      });
      ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((confirmed) => {
        if (confirmed) {
          this.revertChanges(this.selectedPermissionType);
          this.closeDialog();
        }
      });
    } else {
      this.closeDialog();
    }
  }

  closeDialog(): void {
    this.showDialog = false;
    this.isSavingUserPermissions = false;
    this.selectedPermissionType = null;
    this.cdr.markForCheck();
  }

  revertChanges(type: 'consumer' | 'prosumer' | null): void {
    if (type === 'consumer') {
      this.consumerPermissions = { ...this.initialConsumerPermissions };
    } else if (type === 'prosumer') {
      this.prosumerPermissions = { ...this.initialProsumerPermissions };
    } else {
      this.consumerPermissions = { ...this.initialConsumerPermissions };
      this.prosumerPermissions = { ...this.initialProsumerPermissions };
    }
  }

  private captureInitialState(): void {
    this.initialConsumerPermissions = { ...this.consumerPermissions };
    this.initialProsumerPermissions = { ...this.prosumerPermissions };
  }

  private isDirty(type: 'consumer' | 'prosumer' | null): boolean {
    if (!type) {
      return (
        JSON.stringify(this.consumerPermissions) !== JSON.stringify(this.initialConsumerPermissions) ||
        JSON.stringify(this.prosumerPermissions) !== JSON.stringify(this.initialProsumerPermissions)
      );
    }
    const current = type === 'consumer' ? this.consumerPermissions : this.prosumerPermissions;
    const initial = type === 'consumer' ? this.initialConsumerPermissions : this.initialProsumerPermissions;
    return JSON.stringify(current) !== JSON.stringify(initial);
  }
}
